import type { ApiFiltersResult } from "@modules/types.ts"

type ParsedResult = {
    location: string | null
    filterpart: string | null
}

export type PageType = "azm" | "home" | "community" | "news"

const filterPathSessionStorageKey = "filterPathSessionStorageKey"
const locationPartSessionStorageKey = "locationPartSessionStorageKey"

export function setAZMFilterPathToSessionStorage(filterApiResult: ApiFiltersResult, pageType: PageType) {
    const parsedUrl = parseLadiesUrl(filterApiResult.seoUrl, pageType)

    if (parsedUrl.filterpart) {
        sessionStorage.setItem(filterPathSessionStorageKey, parsedUrl.filterpart)
    } else {
        sessionStorage.removeItem(filterPathSessionStorageKey)
    }

    if (parsedUrl.location) {
        sessionStorage.setItem(locationPartSessionStorageKey, parsedUrl.location)
    } else {
        sessionStorage.removeItem(locationPartSessionStorageKey)
    }
}

export function setFilterPathLocationToSessionStorage(pageType: PageType): void {
    const parsedUrl = parseLadiesUrl(window.location.href, pageType)

    if (parsedUrl.filterpart) {
        sessionStorage.setItem(filterPathSessionStorageKey, parsedUrl.filterpart)
    } else {
        if (parsedUrl.location !== sessionStorage.getItem(locationPartSessionStorageKey)) {
            sessionStorage.removeItem(filterPathSessionStorageKey)
        }
    }

    if (parsedUrl.location) {
        sessionStorage.setItem(locationPartSessionStorageKey, parsedUrl.location)
    } else {
        sessionStorage.removeItem(locationPartSessionStorageKey)
    }
}

export function parseLadiesUrl(url: string, pageType: PageType): ParsedResult {
    const result: ParsedResult = { location: null, filterpart: null }

    let segments: string[]
    let queryString: string
    try {
        // Use a base URL to handle relative paths like "/hamburg/sex-anzeigen"
        const parsedUrl = new URL(url, "https://example.com")
        const path = parsedUrl.pathname.replace(/^\/+|\/+$/g, "")
        queryString = parsedUrl.search // e.g. "?suchtext=ana"
        if (!path) return result
        segments = path.split("/")
    } catch {
        return result
    }

    if (pageType === "azm") {
        // AZM pages: /sex-anzeigen/{filterPath} or /{location}/sex-anzeigen/{filterPath}
        const azmIndex = segments.findIndex((s) => s === "sex-anzeigen")
        if (azmIndex === -1) return result

        if (azmIndex > 0) {
            result.location = segments[0]
        }

        // Build filterpart from all path segments after sex-anzeigen and/or query string
        const filterPathSegments = segments.slice(azmIndex + 1)
        const filterPathSegment = filterPathSegments.join("/")

        if (filterPathSegment && queryString) {
            result.filterpart = filterPathSegment + queryString
        } else if (filterPathSegment) {
            result.filterpart = filterPathSegment
        } else if (queryString) {
            result.filterpart = queryString
        }

        return result
    }

    if (pageType === "home") {
        // Home pages: /{location} or / or /home
        if (segments.length === 1 && segments[0] !== "home") {
            result.location = segments[0]
        }
        return result
    }

    if (pageType === "community") {
        // Community pages: /community or /{location}/community
        const communityIndex = segments.indexOf("community")
        if (communityIndex === -1) return result

        if (communityIndex > 0) {
            result.location = segments[0]
        }
        return result
    }

    if (pageType === "news") {
        // News pages: /aktuelles or /{location}/aktuelles
        const newsIndex = segments.indexOf("aktuelles")
        if (newsIndex === -1) return result

        if (newsIndex > 0) {
            result.location = segments[0]
        }
        return result
    }

    return result
}

export function setAZMFilterPathLocationToSessionStorage(newUrl: string) {
    const parsedUrl = parseLadiesUrl(newUrl, "azm")

    if (parsedUrl.filterpart) {
        sessionStorage.setItem(filterPathSessionStorageKey, parsedUrl.filterpart)
    }

    if (parsedUrl.location) {
        sessionStorage.setItem(locationPartSessionStorageKey, parsedUrl.location)
    }
}

export function setAZMFilterPath() {
    const azmLinks: HTMLAnchorElement[] = [
        ...document.querySelectorAll<HTMLAnchorElement>('a[data-pagename="AZM"]'),
        ...Array.from(document.querySelectorAll<HTMLAnchorElement>("a.slidemenuitem")).filter(
            (link) => link.textContent?.trim() === "Anzeigen",
        ),
    ]

    if (azmLinks.length === 0) {
        return
    }

    const locationPathSegment = sessionStorage.getItem(locationPartSessionStorageKey)
    const filterPathSegment = sessionStorage.getItem(filterPathSessionStorageKey)

    if (!filterPathSegment?.toString() && !locationPathSegment?.toString()) {
        for (const link of azmLinks) {
            link.href = "/sex-anzeigen"
        }

        return
    }

    const azmUrl = new URL(window.location.href)

    let path = ""

    if (locationPathSegment) {
        path += `/${locationPathSegment}`
    }

    path += "/sex-anzeigen"

    if (filterPathSegment) {
        // filterPathSegment can be:
        // 1. A path segment: "intimbereich:teilrasiert"
        // 2. A query string: "?suchtext=ana"
        // 3. Both: "intimbereich:teilrasiert?suchtext=ana"
        if (filterPathSegment.startsWith("?")) {
            // Only query string, no path segment
            azmUrl.pathname = path
            azmUrl.search = filterPathSegment
        } else if (filterPathSegment.includes("?")) {
            // Both path segment and query string
            const [pathPart, queryPart] = filterPathSegment.split("?")
            azmUrl.pathname = `${path}/${pathPart}`
            azmUrl.search = `?${queryPart}`
        } else {
            // Only path segment
            azmUrl.pathname = `${path}/${filterPathSegment}`
            azmUrl.search = ""
        }
    } else {
        azmUrl.pathname = path
        azmUrl.search = ""
    }

    for (const link of azmLinks) {
        link.href = azmUrl.toString()
    }
}

export function resetAZMFilterPathFromSessionStorage() {
    sessionStorage.removeItem(filterPathSessionStorageKey)
    sessionStorage.removeItem(locationPartSessionStorageKey)
}
